/**
 * The thread index as the chat API returns it, and how it maps onto assistant-ui's thread list.
 */

export type ThreadDto = {
  id: string;
  title: string;
  pinned_at: string | null;
  archived_at: string | null;
  updated_at: string;
  created_at?: string;
};

/** Metadata the sidebar keeps per thread beyond what assistant-ui models (`custom`). */
type ThreadCustom = { pinned: boolean };

// D1's datetime('now') is UTC without a zone ("2026-11-01 09:30:00").
function parseDbDate(value: string): Date {
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value.replace(" ", "T")}Z`);
}

export function toThreadMetadata(t: ThreadDto) {
  return {
    remoteId: t.id,
    title: t.title,
    status: t.archived_at ? ("archived" as const) : ("regular" as const),
    lastMessageAt: parseDbDate(t.updated_at),
    custom: { pinned: t.pinned_at !== null } satisfies ThreadCustom,
  };
}

export function isPinned(custom: Record<string, unknown> | undefined): boolean {
  return custom?.pinned === true;
}

type TitleSourceMessage = {
  role: string;
  content: readonly { type: string; text?: string }[];
};

/**
 * The first question and answer, which the title model names the thread from. Null until the
 * first answer has text, so titling waits for it (assistant-ui asks again after the run ends).
 */
export function titleConversation(messages: readonly TitleSourceMessage[]) {
  const text = (m: TitleSourceMessage | undefined) =>
    (m?.content ?? [])
      .filter((p) => p.type === "text" && p.text)
      .map((p) => p.text)
      .join(" ")
      .trim();
  const question = text(messages.find((m) => m.role === "user"));
  const answer = text(messages.find((m) => m.role === "assistant"));
  if (!question || !answer) return null;
  return [
    { role: "user" as const, text: question.slice(0, 2000) },
    { role: "assistant" as const, text: answer.slice(0, 2000) },
  ];
}
